import { CacheProvider } from '@emotion/react';
import { createLayeredEmotionCache } from '@fileee/ui';
import { setDefaultOptions } from 'date-fns';
import { MotionConfig } from 'motion/react';
import { Tooltip } from 'radix-ui';
import { lazy, StrictMode, Suspense, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { acceptConsentWithoutBanner } from 'views/consentBanner/consent-utils';
import GlobalSnackBar from 'views/global/SnackBar/GlobalSnackBar';

export type AppContainerType =
  | 'container'
  | 'container-login'
  | 'container-external'
  | 'container-shared'
  | 'module-container';

const UnAuthorizedContainerAppLazy = lazy(
  () => import(/* webpackChunkName : "login-container" */ 'views/UnAuthorizedContainerApp')
);

const AuthorizedContainerAppLazy = lazy(
  () => import(/* webpackChunkName : "main-container" */ 'views/AuthorizedContainerApp')
);

const ModuleContainerAppLazy = lazy(
  () => import(/* webpackChunkName : "module-container" */ 'views/ModuleContainerApp')
);

let root: Root | null = null;

/**
 * Replaces emotion's implicit default cache so every css={{...}} in the tree
 * lands in the "emotion" cascade layer declared in styles/master.scss. The key
 * stays "css" to keep the generated class names ("css-<hash>") unchanged.
 */
const emotionCache = createLayeredEmotionCache({ key: 'css' });

export function renderRootApp(container: AppContainerType, { to = '' }: { to?: string }) {
  const appRoot = document.getElementById('fileee-app');

  if (container === 'container-external' || container === 'module-container') {
    acceptConsentWithoutBanner();
  }

  if (!root) {
    root = createRoot(appRoot);
  }

  root.render(
    <StrictMode>
      {/*
        motion defaults to reducedMotion: "never", so the OS setting is ignored unless we
        opt in here. "user" drops transform and layout animations when the user asks for
        reduced motion but keeps opacity and colour, so fades still signal that something
        entered or left. Covers @fileee/core-sdk too: it resolves to the same motion
        instance, so its task-ui dropdowns read this context.
      */}
      <MotionConfig reducedMotion="user">
        <CacheProvider value={emotionCache}>
          <Suspense fallback={null}>
            <Tooltip.Provider>
              <RootApp key={container} container={container} to={to} />
            </Tooltip.Provider>
            <GlobalSnackBar />
          </Suspense>
        </CacheProvider>
      </MotionConfig>
    </StrictMode>
  );
}

function RootApp({ container, to = '' }: { container: AppContainerType; to?: string }) {
  useEffect(() => {
    setDefaultOptions({ weekStartsOn: 1 });
  }, []);

  switch (container) {
    case 'container': {
      return <AuthorizedContainerAppLazy navigateTo={to} />;
    }
    case 'container-external': {
      return <UnAuthorizedContainerAppLazy handleDocumentRoute={true} />;
    }
    case 'module-container': {
      return <ModuleContainerAppLazy navigateTo={to} />;
    }
    default: {
      return <UnAuthorizedContainerAppLazy handleDocumentRoute={false} />;
    }
  }
}
